import React, { useState } from 'react';
import { X, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import PaymentForm from './PaymentForm';
import { CustomerInfo, formatCurrency } from '../../services/paymentAPI';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  amount: number;
  currency?: string;
  invoiceId?: number;
  customer: CustomerInfo;
  onPaymentSuccess?: (result: any) => void;
  onPaymentError?: (error: any) => void;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  amount,
  currency = 'KWD',
  invoiceId,
  customer,
  onPaymentSuccess,
  onPaymentError,
}) => {
  const [paymentStatus, setPaymentStatus] = useState<'form' | 'processing' | 'success' | 'error'>('form');
  const [paymentResult, setPaymentResult] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  if (!isOpen) return null;

  const handlePaymentSuccess = (result: any) => {
    setPaymentResult(result);
    setPaymentStatus('success');
    if (onPaymentSuccess) {
      onPaymentSuccess(result);
    }
  };

  const handlePaymentError = (error: any) => {
    setErrorMessage((error as any)?.message || 'Payment failed. Please try again.');
    setPaymentStatus('error');
    if (onPaymentError) {
      onPaymentError(error);
    }
  };

  const handleClose = () => {
    setPaymentStatus('form');
    setPaymentResult(null);
    setErrorMessage('');
    onClose();
  };

  const renderPaymentForm = () => (
    <PaymentForm
      amount={amount}
      currency={currency}
      invoiceId={invoiceId}
      customer={customer}
      onSuccess={handlePaymentSuccess}
      onError={handlePaymentError}
      onCancel={handleClose}
    />
  );

  const renderSuccessState = () => (
    <div className="text-center p-8">
      <CheckCircle className="mx-auto h-16 w-16 text-green-500 mb-4" />
      <h2 className="text-2xl font-semibold text-gray-900 mb-2">
        Payment Successful!
      </h2>
      <p className="text-gray-600 mb-4">
        Your payment of {formatCurrency(amount, currency)} has been processed successfully.
      </p>
      
      {paymentResult && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="text-sm text-gray-600 space-y-1">
            <p><strong>Payment ID:</strong> {paymentResult.payment_id}</p>
            {paymentResult.reference && (
              <p><strong>Reference:</strong> {paymentResult.reference}</p>
            )}
            <p><strong>Status:</strong> Completed</p>
          </div>
        </div>
      )}

      <button
        onClick={handleClose}
        className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700"
      >
        Close
      </button>
    </div>
  );

  const renderErrorState = () => (
    <div className="text-center p-8">
      <XCircle className="mx-auto h-16 w-16 text-red-500 mb-4" />
      <h2 className="text-2xl font-semibold text-gray-900 mb-2">
        Payment Failed
      </h2>
      <p className="text-gray-600 mb-4">
        {errorMessage}
      </p>
      
      <div className="space-y-3">
        <button
          onClick={() => setPaymentStatus('form')}
          className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 mr-3"
        >
          Try Again
        </button>
        <button
          onClick={handleClose}
          className="border border-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-50"
        >
          Close
        </button>
      </div>
    </div>
  );

  const renderProcessingState = () => (
    <div className="text-center p-8">
      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4" />
      <h2 className="text-2xl font-semibold text-gray-900 mb-2">
        Processing Payment...
      </h2>
      <p className="text-gray-600">
        Please wait while we process your payment. Do not close this window.
      </p>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={handleClose} />
        </div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {paymentStatus === 'success' ? 'Payment Complete' : 
                 paymentStatus === 'error' ? 'Payment Error' :
                 paymentStatus === 'processing' ? 'Processing Payment' : 'Payment'}
              </h3>
              <button
                onClick={handleClose}
                className="rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="mt-2">
              {paymentStatus === 'form' && renderPaymentForm()}
              {paymentStatus === 'processing' && renderProcessingState()}
              {paymentStatus === 'success' && renderSuccessState()}
              {paymentStatus === 'error' && renderErrorState()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;